import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import {
  CLIENT_APPS_PAGE,
  COMPANIES_PAGE,
  CONTRACT_DETAILS_PAGE,
  CONTRACTS_PAGE,
  FILES_PAGE,
  FORMS_DETAIL_PAGE_V2,
  FORMS_PAGE,
  PAYMENTS_INVOICES_PAGE,
  USERS_PAGE,
} from 'src/constants';
import { InboxNotificationDetailsResponse } from 'src/entities/Notifications';
import { useAppSelector } from 'src/hooks/useStore';
import {
  ContractDetailsPageView,
  ContractPageTabs,
} from 'src/legacy/components/Contracts';
import { useGetContractsQuery } from 'src/services/api';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { useGetFormByIdQuery } from 'src/services/api/formsApi';

import { InboxSidebarItemData } from './InboxSidebar';
import { NotificationContentBreadcrumbs } from './NotificationContentBreadcrumbs';

const useStyles = makeStyles<{ isFileNotification: boolean }>()(
  (theme, props) => ({
    root: {
      padding: props.isFileNotification
        ? theme.spacing(2.5, 0, 1, 2.5)
        : theme.spacing(0),
    },
  }),
);

type Props = {
  selectedNotification: InboxSidebarItemData;
  selectedNotificationDetails: InboxNotificationDetailsResponse | undefined;
  children: React.ReactNode; // This is the content of the notification details (PDFViewer, FilesTable, FormResponse, Invoice)
  className?: string;
};
export const NotificationDetailsContainer = ({
  selectedNotification,
  selectedNotificationDetails,
  className,
  children,
}: Props) => {
  const {
    resourceId,
    notificationEventType,
    channelId,
    avatarInfo,
    refId: responseRefId,
  } = selectedNotification || {};

  const contractTemplateId = selectedNotification?.channelId;
  const isOneOffContract = !contractTemplateId;
  const { data: contracts } = useGetContractsQuery({
    contractTemplateId,
  });
  const { data: formTemplate } = useGetFormByIdQuery(resourceId as string, {
    skip: !resourceId || notificationEventType !== 'formResponse.completed',
  });
  const contract = contracts?.find((c) => c.id === resourceId);
  const invoiceNumber =
    selectedNotificationDetails?.additionalFields?.invoiceNumber ||
    selectedNotification?.resourceId;

  const invoiceId = selectedNotification?.refId;

  const { classes } = useStyles({
    isFileNotification:
      selectedNotification?.notificationEventType === 'files.new' ||
      selectedNotification?.notificationEventType === 'files.created',
  });

  const { data: { extensionsSettings } = {} } = useGetInstallsQuery();
  const { t } = useTranslation();

  const clients = useAppSelector((state) => state.clients.clients);
  const companyDataMap = useAppSelector(
    (state) => state.clients.companyDataMap,
  );

  const resolvedChannelName = useMemo(() => {
    if (selectedNotificationDetails?.additionalFields?.channelName) {
      return selectedNotificationDetails.additionalFields.channelName;
    }
    if (!selectedNotification?.channelRecipientId) return '';
    if (selectedNotification.channelRecipientType === 'company') {
      return (
        companyDataMap[selectedNotification.channelRecipientId]?.name || ''
      );
    }
    if (selectedNotification.channelRecipientType === 'client') {
      const client = clients.find(
        (c) => c.id === selectedNotification.channelRecipientId,
      );
      if (!client) return '';
      return `${client.fields.givenName} ${client.fields.familyName}`.trim();
    }
    return '';
  }, [
    selectedNotificationDetails?.additionalFields?.channelName,
    selectedNotification?.channelRecipientId,
    selectedNotification?.channelRecipientType,
    companyDataMap,
    clients,
  ]);

  const items = useMemo(() => {
    switch (notificationEventType) {
      case 'formResponse.completed':
      case 'forms.completed':
        return [
          { label: 'Forms', path: `${FORMS_PAGE.path}` },
          {
            label: formTemplate?.fields.name || '',
            path: `${FORMS_DETAIL_PAGE_V2.path}?formId=${resourceId}&view=see_responses`,
          },
          {
            label: avatarInfo?.name || '',
            path: `${FORMS_PAGE.path}/submit?formId=${resourceId}&responseRefId=${responseRefId}`,
          },
        ];
      case 'invoice.paid':
      case 'invoice.processing':
      case 'invoice.paymentFailed':
        return [
          {
            label: 'Invoices',
            path: `${PAYMENTS_INVOICES_PAGE.path}`,
          },
          {
            label: invoiceNumber,
            path: `${PAYMENTS_INVOICES_PAGE.path}/details?invoiceId=${invoiceId}`,
          },
        ];
      case 'files.new':
      case 'files.created':
        return [
          { label: 'Files', path: `${FILES_PAGE.path}` },
          {
            label: avatarInfo?.name || '',
            path: `${FILES_PAGE.path}?channelId=${channelId}`,
          },
        ];
      case 'esign.completed':
      case 'contract.signed':
        return [
          { label: 'Contracts', path: `${CONTRACTS_PAGE.path}` },
          {
            label: contract?.name || '',
            path: isOneOffContract
              ? `${CONTRACTS_PAGE.path}?view=${ContractPageTabs.Submissions}`
              : `${CONTRACT_DETAILS_PAGE.path}?templateId=${contractTemplateId}&view=${ContractDetailsPageView.SubmissionRequests}`,
          },
          {
            label: avatarInfo?.name || '',
            path: `${CONTRACTS_PAGE.path}/submission?contractId=${resourceId}`,
          },
        ];
      case 'internalChat.mentioned':
      case 'channel.mentioned': {
        const recipientType = selectedNotification.channelRecipientType;
        // Channels not tied to a client or company (e.g. group/custom messaging
        // channels) — fall back to a single channel-name crumb so we don't
        // render a broken "Companies > undefined" link.
        if (recipientType !== 'client' && recipientType !== 'company') {
          return resolvedChannelName ? [{ label: resolvedChannelName }] : [];
        }
        const isClient = recipientType === 'client';
        return [
          {
            label: isClient
              ? t(($) => $.client, { count: 2 })
              : t(($) => $.company, { count: 2 }),
            path: isClient ? `${USERS_PAGE.path}` : `${COMPANIES_PAGE.path}`,
          },
          {
            label: resolvedChannelName,
            path: isClient
              ? `${USERS_PAGE.path}/details?clientUserId=${selectedNotification.channelRecipientId}`
              : `${COMPANIES_PAGE.path}/${selectedNotification.channelRecipientId}`,
          },
        ];
      }
      case 'customApp.action':
        if (selectedNotificationDetails && extensionsSettings) {
          const appName =
            extensionsSettings[selectedNotificationDetails?.resourceId]?.name ??
            '';
          const queryParams = new URLSearchParams({
            id: selectedNotificationDetails?.resourceId,
          });
          const ctaQueryParams = new URLSearchParams({
            id: selectedNotificationDetails?.resourceId,
            ...(selectedNotificationDetails?.deliveryTargets?.inProduct
              ?.ctaParams ?? {}),
          });
          return [
            {
              label: appName,
              path: `${CLIENT_APPS_PAGE.path}?${queryParams.toString()}`,
            },
            {
              label: avatarInfo?.name || '',
              path: `${CLIENT_APPS_PAGE.path}?${ctaQueryParams.toString()}`,
            },
          ];
        }

        return [];

      default:
        return [];
    }
  }, [
    channelId,
    contract?.name,
    formTemplate?.fields.name,
    contractTemplateId,
    notificationEventType,
    resourceId,
    avatarInfo,
    extensionsSettings,
    resolvedChannelName,
    selectedNotification.channelRecipientId,
    selectedNotification.channelRecipientType,
    selectedNotificationDetails?.additionalFields?.channelName,
  ]);

  return (
    <>
      <div className={clsx(classes.root, className)}>
        <NotificationContentBreadcrumbs items={items} />
      </div>
      {children}
    </>
  );
};
